/**
 * Browser-side reCAPTCHA v3 loader shared by the native forms.
 *
 * The script is injected on first interaction rather than on page load, so
 * visitors who never touch a form do not pay for it. The load promise is
 * module-level so several forms on one page share a single script.
 */

const RECAPTCHA_SITE_KEY = (process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? "").trim();
const RECAPTCHA_SRC = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(RECAPTCHA_SITE_KEY)}`;

/** False when the site key was absent at build time; forms disable themselves. */
export const RECAPTCHA_READY = RECAPTCHA_SITE_KEY.length > 0;

let recaptchaLoad: Promise<void> | null = null;

export function loadRecaptcha(): Promise<void> {
  if (typeof window === "undefined" || !RECAPTCHA_READY) return Promise.resolve();
  if (window.grecaptcha) return Promise.resolve();
  if (!recaptchaLoad) {
    recaptchaLoad = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = RECAPTCHA_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        recaptchaLoad = null;
        reject(new Error("reCAPTCHA script failed to load"));
      };
      document.head.appendChild(script);
    });
  }
  return recaptchaLoad;
}

/** Fire-and-forget warm-up, safe to call from focus handlers. */
export function preloadRecaptcha(): void {
  void loadRecaptcha().catch(() => {});
}

export async function getRecaptchaToken(action: string): Promise<string> {
  await loadRecaptcha();
  return new Promise<string>((resolve, reject) => {
    window.grecaptcha.ready(() => {
      window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action }).then(resolve, reject);
    });
  });
}
